import {
  createContext,
  Dispatch,
  useMemo,
  useState,
  useContext,
} from 'react';

import { toast } from 'react-toastify';

import { PAGE_LOGIN } from 'containers/Login';
import accessToken from 'utils/accessToken';
import authToken from 'utils/authToken';

export interface AuthState {
  username: string | null;
  fullname: string | null;
  admin: boolean;
  multi: boolean;
  organization: string | null;
}

interface AuthProviderI {
  authState: AuthState;
  setStateAuth: Dispatch<AuthState>;
}

const AuthContext = createContext<AuthProviderI | null>(null);

/* === use === */
const useAuth = () => {
  const authContext = useContext(AuthContext);

  return {
    authState: authContext?.authState as AuthState,
    setAuthState: authContext?.setStateAuth as Dispatch<AuthState>,
  };
};

interface AuthProviderProps {
  children: any | Element[];
}
/* === provider === */
const AuthProvider = ({ children }: AuthProviderProps) => {
  let extractedData: AuthState = {} as AuthState;

  const jwtToken = accessToken.get();
  try {
    extractedData = authToken.extractData(jwtToken);
  } catch {
    accessToken.set('');
    if (jwtToken !== '' && window.location.pathname !== PAGE_LOGIN) {
      toast.error('Credentials are invalid');
    }
  }

  const [authState, setStateAuth] = useState(extractedData);

  const contextValue = useMemo<AuthProviderI>(() => ({
    authState,
    setStateAuth,
  }), [authState]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/* ===== Functions ===== */
export const hasPermissionOnSCHCTemplate = (
  authState: AuthState,
  orgTemplate?: string,
) => {
  // multi-org user (either Admin or Operator) has full permission on Template
  if (authState.multi === true) {
    return true;
  }

  // orgTemplate is undefined in the Template List page.
  // orgTemplate is defined and not empty in the Organization Template View page,
  // orgTemplate is defined and empty in the Common/Instance Template View page.
  // Admin user has permission only on the List page and Organization Template View page.
  return (orgTemplate === undefined || orgTemplate !== '') && authState.admin === true;
};

export { AuthProvider, useAuth };
